import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import PersonCell from '../../../components/ui/PersonCell';

const useMyVisitApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lookups, setLookups] = useState({ gates: [], gateGroups: [] });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/my-visits/lookups/gates').then((res) => {
      setLookups(res.data?.data || { gates: [], gateGroups: [] });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/my-visits?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      const res = await api.post('/v1/my-visits', formData);
      setRefreshTrigger((p) => p + 1);
      return { success: true, data: res.data?.data };
    } catch {
      setFetching(false);
      return { success: false };
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/my-visits/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return { success: true };
    } catch {
      setFetching(false);
      return { success: false };
    }
  }, []);

  const cancelVisit = useCallback(async (id) => {
    try {
      await api.post(`/v1/my-visits/${id}/cancel`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const showInvite = useCallback(async (id) => {
    try {
      const res = await api.post(`/v1/my-visits/${id}/regenerate-link`);
      return res.data?.data || null; // { inviteLink, smsSent }
    } catch {
      return null;
    }
  }, []);

  const statusLabel = useCallback((row) => {
    const effective = row.status === 'approved' && row.validTo && new Date(row.validTo) < new Date()
      ? 'expired'
      : row.status;
    const map = {
      waiting: t('visit.statusWaiting'),
      approved: t('visit.statusApproved'),
      rejected: t('visit.statusRejected'),
      canceled: t('visit.statusCanceled'),
      expired: t('visit.statusExpired'),
    };
    return map[effective] || effective;
  }, [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'visitorFullName', label: t('visit.visitorFullName'), options: { filter: false, sort: false } },
    { name: 'visitorPhone', label: t('visit.visitorPhone'), options: { filter: false, sort: false } },
    { name: 'hostEmployeeName', label: t('visit.hostEmployee'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.hostEmployeeProfilePicture} />
      ), filter: false, sort: false } },
    {
      name: 'validFrom',
      label: t('visit.validFrom'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? new Date(v).toLocaleString(i18n.language) : '-') },
    },
    {
      name: 'validTo',
      label: t('visit.validTo'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? new Date(v).toLocaleString(i18n.language) : '-') },
    },
    {
      name: 'status',
      label: t('visit.status'),
      options: {
        filter: true,
        sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['waiting', 'approved', 'rejected', 'canceled'],
          renderValue: (v) => statusLabel({ status: v }),
        },
        customFilterListOptions: { render: (v) => statusLabel({ status: v }) },
      },
    },
    { name: 'rejectionReason', label: t('visit.denyReason'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
  ], [t, i18n.language, statusLabel, data]);

  const searchGates = useCallback(async (q) => {
    const list = lookups.gates || [];
    return (q ? list.filter((g) => g.name.toLowerCase().includes(q.toLowerCase())) : list)
      .map((g) => ({ id: g.id, name: g.name }));
  }, [lookups]);

  const getGateById = useCallback(async (id) => (lookups.gates || []).find((g) => g.id === id) || null, [lookups]);

  const objectSchema = useMemo(() => [
    { name: 'visitorFirstName', label: t('visit.visitorFirstName'), type: 'text', grid: 6, required: true },
    { name: 'visitorLastName', label: t('visit.visitorLastName'), type: 'text', grid: 6, required: true },
    { name: 'visitorPhone', label: t('visit.visitorPhone'), type: 'phone', grid: 12, required: true },
    { name: 'visitorNationalId', label: t('visit.visitorNationalId'), type: 'text', grid: 6 },
    { name: 'visitorCompany', label: t('visit.visitorCompany'), type: 'text', grid: 6 },
    { name: 'vehiclePlate', label: t('visit.vehiclePlate'), type: 'text', grid: 6 },
    { name: 'gateIds', label: t('visit.gates'), type: 'asyncSelect', grid: 12, isMulti: true, onSearch: searchGates, onGetById: getGateById },
    { name: 'validFrom', label: t('visit.validFrom'), type: 'datetime', grid: 6, required: true },
    { name: 'validTo', label: t('visit.validTo'), type: 'datetime', grid: 6, required: true },
    { name: 'dailyTimeStart', label: t('visit.dailyTimeStart'), type: 'time', grid: 6 },
    { name: 'dailyTimeEnd', label: t('visit.dailyTimeEnd'), type: 'time', grid: 6 },
    { name: 'notes', label: t('visit.notes'), type: 'textarea', grid: 12 },
  ], [t, searchGates, getGateById]);

  return { objectSchema, columns, data, fetching, pagination, create, update, cancelVisit, showInvite, statusLabel };
};

export default useMyVisitApi;
